import { useEffect, useState } from "react";
import {
  Collection,
  CollectionFill,
  Compass,
  CompassFill,
  Pencil,
  PencilFill,
  QuestionOctagon,
  QuestionOctagonFill
} from "react-bootstrap-icons";
import { useCookies } from "react-cookie";
import styled from "styled-components";
import { DeletePage } from "./pages/delete.page";
import { Header } from "./components/Header";
import { ErrorMessage } from "./components/ErrorMessage";
import { CreatePage } from "./pages/create.page";
import { Button } from "./components/Button";
import { CheckPage } from "./pages/check.page";
import { FindPage } from "./pages/find.page";
import { StatsPage } from "./pages/stats.page";
import { NavigationPage } from "./pages/navigation.page";
import { Mode } from "./types/Mode";
import { useMode } from "./context/ModeContext";
import { ManagePage } from "./pages/manage.page";
import { HelpText } from "./components/HelpText";
import { WarehouseService } from "./api/warehouse.service";

const AppWrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: calc(var(--vh, 1vh) * 100);
`;

const Content = styled.div`
  flex: 1;
  overflow-y: auto;
  //   margin: 0 10px;
  padding: 15px 10px 10px 10px;
  // padding: 10px 20px;
`;

const Navigation = styled.div`
  width: 100%;
  text-align: center;
  padding: 10px 20px;
  background: #0c0c0d;
`;

const Item = styled.div`
  width: 70px;
  display: inline-block;
  cursor: pointer;
  margin: 0 4px;
  border-radius: 6px;

  & > .icon {
    width: 20px;
    height: 20px;
    margin: 5px auto 5px auto;
  }

  & > .text {
    font-size: 14px;
    color: white;
  }

  &:first-child {
    margin-left: 0;
  }

  &:last-child {
    margin-right: 0;
  }
`;

const Input = styled.input`
  display: block;
  margin: 10px auto;
  width: 250px;
  font-size: 16px;
  text-align: center;
  padding: 5px;
  border-radius: 4px;
  border: 1px solid black;
  padding: 7px;
`;

export const Locations = styled.div`
  text-align: center;
  margin-top: 10px;
`;

export const Location = styled.div`
  margin-top: 5px;
  cursor: pointer;
`;

export default function UI() {
  useEffect(() => {
    const setVh = () => {
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty("--vh", `${vh}px`);
    };

    setVh();
    window.addEventListener("resize", setVh);
    return () => window.removeEventListener("resize", setVh);
  }, []);

  const { mode, transition, changeMode, setMode } = useMode();

  function loadContent() {
    switch (mode) {
      case Mode.CREATE:
        return <CreatePage />;
      case Mode.DELETE:
        return <DeletePage />;
      case Mode.CHECK:
        return <CheckPage />;
      case Mode.FIND:
        return <FindPage />;
      case Mode.MANAGE:
        return <ManagePage />;
      case Mode.STATS:
        return <StatsPage />;
      case Mode.NAVIGATION:
        return <NavigationPage />;
      default:
        return <div>404</div>;
    }
  }

  const [cookie, setCookie] = useCookies(["access_key", "disabled_information_messages"]);
  const [accessKeyField, setAccessKeyField] = useState("");

  const validateSignIn = () => {
    if (!accessKeyField) {
      setSignInError("Podaj klucz dostępu");
      return false;
    }
    return true;
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      signIn();
    }
  };

  const [signInError, setSignInError] = useState("");
  const [isVerifying, setIsVerifying] = useState(false);

  async function signIn() {
    if (!validateSignIn()) return;

    setSignInError("");
    setIsVerifying(true);

    const startTime = Date.now();

    try {
      await WarehouseService.fetchStats(accessKeyField);

      const now = new Date();
      const expireAt = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);

      const duration = Date.now() - startTime;
      const minDuration = 600;

      if (duration < minDuration) {
        await new Promise((resolve) => setTimeout(resolve, minDuration - duration));
      }

      setCookie("access_key", accessKeyField, { expires: expireAt, path: "/" });
      setMode(Mode.FIND);
      setAccessKeyField("");
    } catch (err: any) {
      const duration = Date.now() - startTime;
      if (duration < 400) {
        await new Promise((resolve) => setTimeout(resolve, 400 - duration));
      }

      if (!err.response) {
        setSignInError("Błąd połączenia z serwerem.");
      } else if (err.response.status === 401) {
        setSignInError("Nieprawidłowy klucz dostępu.");
      } else {
        setSignInError("Wystąpił nieoczewinany błąd serwera.");
      }
    } finally {
      setIsVerifying(false);
    }
  }

  if (!cookie.access_key) {
    return (
      <>
        <Header title={"DOSTĘP"} subtitle={"DO MAGAZYNU"} />
        <Content>
          <HelpText alwaysVisible>Wprowadź klucz dostępu.</HelpText>
          <Input
            type="password"
            autoFocus
            value={accessKeyField}
            onKeyDown={handleKeyDown}
            onChange={(e) => {
              setAccessKeyField(e.target.value);
              if (signInError) setSignInError("");
            }}
            placeholder="Klucz dostępu"
            name="key"
          />

          <Button onClick={() => signIn()} text={isVerifying ? "Sprawdzanie..." : "Zapisz"} disabled={isVerifying} />

          {signInError && <ErrorMessage>{signInError}</ErrorMessage>}

          <HelpText alwaysVisible>Aby otrzymać klucz dostępu skontaktuj się z administratorem aplikacji.</HelpText>
        </Content>
      </>
    );
  }

  return (
    <>
      <AppWrapper>
        <Header
          title={
            mode === Mode.FIND
              ? "ZNAJDOWANIE"
              : mode === Mode.MANAGE
                ? "ZARZĄDZANIE"
                : mode === Mode.STATS
                  ? "ZAPEŁNIENIE"
                  : mode === Mode.NAVIGATION
                    ? "NAWIGACJA"
                    : ""
          }
          subtitle=""
        />
        <Content>{loadContent()}</Content>
        <Navigation>
          <Item onClick={() => changeMode(Mode.FIND)}>
            <div className="icon">
              {mode === Mode.FIND ? <CompassFill size={20} color="white" /> : <Compass size={20} color="white" />}
            </div>
            <div className="text">Znajdź</div>
          </Item>
          <Item onClick={() => changeMode(Mode.MANAGE)}>
            <div className="icon">
              {mode === Mode.MANAGE ? <PencilFill size={20} color="white" /> : <Pencil size={20} color="white" />}
            </div>
            <div className="text">Zarządzaj</div>
          </Item>
          <Item
            onClick={() =>
              setCookie("disabled_information_messages", !cookie.disabled_information_messages, {
                maxAge: 60 * 60 * 24 * 14
              })
            }
          >
            <div className="icon">
              {!cookie.disabled_information_messages ? (
                <QuestionOctagonFill size={20} color="yellow" />
              ) : (
                <QuestionOctagon size={20} color="yellow" />
              )}
            </div>
            <div className="text">Pomoc</div>
          </Item>
          <Item onClick={() => changeMode(Mode.NAVIGATION)}>
            <div className="icon">
              {mode === Mode.NAVIGATION ? (
                <CollectionFill size={20} color="white" />
              ) : (
                <Collection size={20} color="white" />
              )}
            </div>
            <div className="text">Więcej</div>
          </Item>
        </Navigation>
      </AppWrapper>
    </>
  );
}
